import Logger from '../util/Logger';
import DateTime from '../models/DateTime';
import { popinApi, popinMultipartApi } from './api';
import { AuthMethod } from '../models/AuthMethod';

/**
 * Generic function to put updates to tenant settings
 *
 * You should call a more specific edit function instead of this directly
 *
 * @param tenantId
 * @param payload
 * @returns {Promise<{tenantId: *, payload: *}>}
 */
const editTenantSettings = (tenantId, payload) => {
  Logger.info(`calling editTenantSettings() with: ${tenantId}`);
  Logger.info(payload);

  return Promise.resolve().then(() => {
    if (!payload) {
      Logger.info('reject editTenantSettings()');
      throw Error('Must pass a payload as an argument');
    } else if (!tenantId) {
      Logger.info('reject editTenantSettings()');
      throw Error('Must pass a tenant Id as an argument');
    } else {
      Logger.info('resolve editTenantSettings()');
      return popinApi.put(`/admin/tenants/${tenantId}`, payload);
    }
  });
};

/**
 * Save SAML settings providing metadata via an XML file
 * @param tenantId
 * @param xml
 */
const saveSamlFileMetadata = (tenantId, xml) => {
  Logger.info(`calling saveSamlFileMetadata with ${tenantId}`);
  Logger.info(xml);

  return Promise.resolve().then(() => {
    if (!tenantId) {
      throw Error('Must pass a tenant Id as an argument');
    }
    if (!xml) {
      throw Error('Must pass XML data as an argument');
    }

    const formData = new FormData();
    const blobProps = { type: 'application/xml' };
    formData.set('metadata', new Blob([xml], blobProps), 'saml_import.xml');
    return popinMultipartApi.post(`/admin/tenants/${tenantId}/saml/idp/import`, formData);
  });
};

/**
 * Admin endpoint API functions
 *
 * @type {{fetchTenantStats: (function(*=, *=): Promise<void>), fetchUsers: (function(*=, *=): Promise<void>), fetchActivitiesByUseCase: (function(*=, *=): Promise<void>), fetchHeroes: (function(*=, *=): Promise<void>), fetchPopins: (function(*=, *=): Promise<void>), fetchWhitelist: (function(*=): Promise<void>), addNewAdmins: (function(*=, *=): Promise<void>), removeAdmin: (function(*=, *=): Promise<void>), listTenantAdmins: (function(*=): Promise<void>), updateTenantName: (function(*=, *): Promise<{tenantId: *, payload: *}>), updateAuthMethod: adminApi.updateAuthMethod, updateConfigurationDefaults: (function(*=, *=, *=, *=, *=): Promise<{tenantId: *, payload: *}>), reassignTenantOwner: (function(*=, *=): Promise<void>), sendTestEmails: (function(*=, *=): Promise<void>), addNewAuthorizedCreators: (function(*=, *=): Promise<void>), removeAuthorizedCreator: (function(*=, *=): Promise<void>)}}
 */
export const adminApi = {

  fetchTenantStats: (tenantId, numWeeks) => {
    Logger.info('fetchTenantStats called');
    const tz = DateTime.getCurrentTimeZone();
    let url = `/admin/tenants/${tenantId}/stats?time_zone=${tz}`;
    if (numWeeks) { // if numWeeks !== null, we tack a param on that limits the timeframe of the data returned
      url += `&num_weeks=${numWeeks}`;
    }
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject fetchTenantStats()');
        throw Error('Must pass a tenant ID as a param');
      } else {
        Logger.info('resolve fetchTenantStats()');
        return popinApi.get(url);
      }
    });
  },
  /**
   * Given a tenant Id, fetches verified and guest users list. If last60Days boolean is present, then data is limited to last 60 days
   * @param tenantId
   * @param numWeeks number
   * @returns {Promise<void>}
   */
  fetchUsers: (tenantId, numWeeks) => {
    const tz = DateTime.getCurrentTimeZone();
    let url = `/admin/tenants/${tenantId}/stats/activeusers?time_zone=${tz}`;
    if (numWeeks) { // if numWeeks !== null, we tack a param on that limits the timeframe of the data returned
      url += `&num_weeks=${numWeeks}`;
    }
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject fetchUsers()');
        throw Error('Must pass a tenant ID as a param');
      } else {
        Logger.info('resolve fetchUsers()');
        return popinApi.get(url);
      }
    });
  },
  /**
   * Given a tenant Id, fetches all activities counts by use case. If last60Days boolean is present, then data is limited to last 60 days
   * @param tenantId
   * @param numWeeks number
   * @returns {Promise<void>}
   */
  fetchActivitiesByUseCase: (tenantId, numWeeks) => {
    const tz = DateTime.getCurrentTimeZone();
    let url = `/admin/tenants/${tenantId}/stats/activity?time_zone=${tz}`;
    if (numWeeks) { // if numWeeks !== null, we tack a param on that limits the timeframe of the data returned
      url += `&num_weeks=${numWeeks}`;
    }
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject fetchActivitiesByUseCase()');
        throw Error('Must pass a tenant ID as a param');
      } else {
        Logger.info('resolve fetchActivitiesByUseCase()');
        return popinApi.get(url);
      }
    });
  },
  /**
   * Given a tenant id fetches the list of heroes
   *
   * @param tenantId The id of the user tenant
   * @param numWeeks number
   * @returns {Promise<object>} List of mini user objects defined by server
   */
  fetchHeroes: (tenantId, numWeeks) => {
    const tz = DateTime.getCurrentTimeZone();
    let url = `/admin/tenants/${tenantId}/heroes?time_zone=${tz}`;
    if (numWeeks) { // if numWeeks !== null, we tack a param on that limits the timeframe of the data returned
      url += `&num_weeks=${numWeeks}`;
    }
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject fetchHeroes()');
        throw Error('Must pass a tenant Id as an argument');
      } else {
        Logger.info('resolve fetchHeroes()');
        return popinApi.get(url);
      }
    });
  },
  /**
   * Given a tenant id fetches the list of events
   *
   * @param tenantId The id of the user tenant
   * @param numWeeks number
   * @returns {Promise<object>} List of mini event objects defined by server
   */
  fetchPopins: (tenantId, numWeeks) => {
    const tz = DateTime.getCurrentTimeZone();
    let url = `/admin/tenants/${tenantId}/popins?time_zone=${tz}`;
    if (numWeeks) { // if numWeeks !== null, we tack a param on that limits the timeframe of the data returned
      url += `&num_weeks=${numWeeks}`;
    }
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject fetchPopins()');
        throw Error('Must pass a tenant Id as an argument');
      } else {
        Logger.info('resolve fetchPopins()');
        return popinApi.get(url);
      }
    });
  },
  /**
   * Given a tenant id fetches a whitelisted list of heroes (for self launch restriction)
   *
   * @param tenantId The id of the user tenant
   * @returns {Promise<object>} List of mini user objects defined by server
   */
  fetchWhitelist: (tenantId) => {
    Logger.info(`calling fetchWhitelist() with: ${tenantId}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject fetchWhitelist()');
        throw Error('Must pass a tenant Id as an argument');
      } else {
        Logger.info('resolve fetchWhitelist()');
        return popinApi.get(`/admin/tenants/${tenantId}/heroes/whitelist`);
      }
    });
  },

  /**
   * Add new admins
   *
   * This function accepts a list of emails and will abstract calling the server's API which only
   * accepts a single email per call. Maybe the server can be convinced to take a list...
   *
   * @param tenantId id of the tenant
   * @param emails list of new emails - many or one
   * @returns Promise<object> list of account administrator items
   */
  addNewAdmins: (tenantId, emails) => {
    Logger.info(`calling addNewAdmins() with: ${tenantId}: ${emails}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject addNewAdmins()');
        throw Error('Must pass tenantId as argument');
      } else if (!emails || emails.length === 0) {
        Logger.info('reject addNewAdmins()');
        throw Error('Must pass list of emails as argument');
      } else {
        Logger.info('Resolve addNewAdmins()');
        return popinApi.post(`/admin/tenants/${tenantId}/admins/add`, { emails });
      }
    });
  },

  /**
   * Remove existing admin
   *
   * @param tenantId id of the tenant
   * @param idOrEmail A userId or an email - differentiated based on if it contains an @ sign
   * @returns Promise<object> list of account administrator items
   */
  removeAdmin: (tenantId, idOrEmail) => {
    Logger.info(`calling removeAdmin() with: ${tenantId}: ${idOrEmail}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject removeAdmin()');
        throw Error('Must pass tenantId as argument');
      } else if (!idOrEmail) {
        Logger.info('reject removeAdmin()');
        throw Error('Must pass userId or admin as argument');
      } else {
        Logger.info('Resolve removeAdmin()');
        const payload = {};
        if (idOrEmail.indexOf('@') < 0) {
          // No @ sign, this is a user_id
          payload.user_id = idOrEmail;
        } else {
          // @ sign, this is an email
          payload.email = idOrEmail;
        }
        return popinApi.post(`/admin/tenants/${tenantId}/admins/remove`, payload);
      }
    });
  },

  /**
   * Given a tenant id fetches the list of Account Administrators
   *
   * @param tenantId The id of the user tenant
   * @returns {Promise<List<*>>} List of mini user objects
   */
  listTenantAdmins: (tenantId) => {
    Logger.info(`calling listTenantAdmins() with: ${tenantId}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject listTenantAdmins()');
        throw Error('Must pass a tenant Id as an argument');
      } else {
        Logger.info('resolve listTenantAdmins()');
        return popinApi.get(`/admin/tenants/${tenantId}/admins`);
      }
    });
  },

  /**
   * Updates the name for this tenant
   *
   * @param tenantId The id of the user tenant
   * @param name The new name
   * @returns {Promise<{tenantId: *, payload: *}>}
   */
  updateTenantName: (tenantId, name) => {
    Logger.info(`calling updateTenantName() with: ${tenantId} ${name}`);
    return editTenantSettings(tenantId, { name });
  },

  /**
   * Updates the name for this tenant
   *
   * @param tenantId The id of the user tenant
   * @param method The new AuthMethod
   * @param samlUrl The SAML IDP url - only used if method is SSO
   * @param samlFile The SAML xml file metadata - only used if method is SSO and takes priority over the URL
   * @returns {Promise<{tenantId: *, payload: *}>}
   */
  updateAuthMethod: (tenantId, method, samlUrl, samlFile) => {
    Logger.info(`calling updateAuthMethod() with: ${tenantId} ${method} ${samlUrl}`);
    const payload = {};
    if (method) {
      payload.authentication_strategy = method;
    }

    if (samlUrl) {
      payload.saml_idp_metadata_url = samlUrl;

      // Clear out the XML if it exists
      payload.saml_idp_xml_metadata = null;
    }

    if (samlFile) {
      // Clear out the IDP Url if it exists
      payload.saml_idp_metadata_url = null;
      return saveSamlFileMetadata(tenantId, samlFile)
        .then(() => editTenantSettings(tenantId, payload));
    } else {
      if (method === AuthMethod.DEFAULT) {
        // Clear out the IDP Url if it exists
        payload.saml_idp_metadata_url = null;

        // Clear out the XML if it exists
        payload.saml_idp_xml_metadata = null;
      }
      return editTenantSettings(tenantId, payload);
    }
  },

  /**
   * Updates the self launch restriction for this tenant
   *
   * @param tenantId The id of the user tenant
   * @param restrictSelfLaunch Boolean to restrict popin creation
   * @param showCodeOfConduct Boolean to show Code of Conduct
   * @param allowPdfs Boolean to allow PDF attachments
   * @param allowImages Boolean to allow Image attachments
   * @param enableShortLinks Boolean to enable or disable short links
   * @returns {Promise<{tenantId: *, payload: *}>}
   */
  updateConfigurationDefaults: (
    tenantId, restrictSelfLaunch, showCodeOfConduct, allowPdfs, allowImages, enableShortLinks) => {
    Logger.info(
      `calling updateConfigurationDefaults() with: ${tenantId} ${restrictSelfLaunch} ${showCodeOfConduct} ${allowPdfs} ${allowImages} ${enableShortLinks}`);
    const payload = {};

    if (restrictSelfLaunch !== undefined && restrictSelfLaunch !== null) {
      payload.restrict_self_launch = !!restrictSelfLaunch;
    }

    if (showCodeOfConduct !== undefined && showCodeOfConduct !== null) {
      payload.show_code_of_conduct = !!showCodeOfConduct;
    }

    if (allowPdfs !== undefined && allowPdfs !== null) {
      payload.answer_media_attachments = !!allowPdfs;
    }

    if (allowImages !== undefined && allowImages !== null) {
      payload.answer_media = !!allowImages;
    }

    if (enableShortLinks !== undefined && enableShortLinks !== null) {
      payload.short_share_link = !!enableShortLinks;
    }
    return editTenantSettings(tenantId, payload);
  },

  /**
   * Re-assign the tenant owner to the new email
   *
   * @param tenantId the Id of the tenant
   * @param email the email of the new tenant owner
   * @returns {Promise<any>} Tenant object
   */
  reassignTenantOwner: (tenantId, email) => {
    Logger.info(`calling reassignTenantOwner() with ${tenantId} ${email}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        throw Error('Must pass a tenant Id as an argument');
      }
      if (!email) {
        throw Error('Must pass a new Owner email as an argument');
      }

      return popinApi.post(`/admin/tenants/${tenantId}/owner`, {
        tenant_id: tenantId,
        new_owner_email: email
      });
    });
  },

  /**
   * Send a test email
   * @param tenantId
   * @param emails
   */
  sendTestEmails: (tenantId, emails) => {
    Logger.info(`calling sendTestEmails with ${tenantId} ${emails}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        throw Error('Must pass a tenant Id as an argument');
      } else if (!emails || emails.length === 0) {
        throw Error('Must pass list of emails as argument');
      } else {
        return popinApi.post(`/admin/tenants/${tenantId}/testemail`, {
          emails: emails
        });
      }
    });
  },

  /**
   * Add new authorized creators
   *
   * This function accepts a list of emails and will abstract calling the server's API which only
   * accepts a single email per call. Maybe the server can be convinced to take a list...
   *
   * @param tenantId id of the tenant
   * @param emails list of new emails - many or one
   * @returns Promise<object> list of authorized creator items
   */
  addNewAuthorizedCreators: (tenantId, emails) => {
    Logger.info(`calling addNewAuthorizedCreators() with: ${tenantId}: ${emails}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject addNewAuthorizedCreators()');
        throw Error('Must pass tenantId as argument');
      } else if (!emails || emails.length === 0) {
        Logger.info('reject addNewAuthorizedCreators()');
        throw Error('Must pass list of emails as argument');
      } else {
        Logger.info('Resolve addNewAuthorizedCreators()');
        return popinApi.post(`/admin/tenants/${tenantId}/heroes/whitelist/add`, { emails });
      }
    });
  },

  /**
   * Remove existing authorized creator
   *
   * @param tenantId id of the tenant
   * @param idOrEmail A userId or an email - differentiated based on if it contains an @ sign
   * @returns Promise<object> list of authorized creator items
   */
  removeAuthorizedCreator: (tenantId, idOrEmail) => {
    Logger.info(`calling removeAuthorizedCreator() with: ${tenantId}: ${idOrEmail}`);
    return Promise.resolve().then(() => {
      if (!tenantId) {
        Logger.info('reject removeAuthorizedCreator()');
        throw Error('Must pass tenantId as argument');
      } else if (!idOrEmail) {
        Logger.info('reject removeAuthorizedCreator()');
        throw Error('Must pass userId or admin as argument');
      } else {
        Logger.info('Resolve removeAuthorizedCreator()');
        const payload = {};
        if (idOrEmail.indexOf('@') < 0) {
          // No @ sign, this is a user_id
          payload.user_id = idOrEmail;
        } else {
          // @ sign, this is an email
          payload.email = idOrEmail;
        }
        return popinApi.post(`/admin/tenants/${tenantId}/heroes/whitelist/remove`, payload);
      }
    });
  }
};
